import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronLeft } from 'lucide-react';

import { Button, Card, Input } from '@llura/ui';
import type { Locale } from '@llura/constants';

import { useCoachStore, type CoachGender, type CoachTone } from '../../stores/coach.store';
import {
  BOUNDARY_CATEGORIES,
  useCovenantStore,
  type BoundaryCategory,
  type BoundaryLevel,
} from '../../stores/covenant.store';
import { usePersonalityStore } from '../../stores/personality.store';
import {
  useRitualStore,
  type RitualIntensity,
  type RitualPeriod,
} from '../../stores/ritual.store';
import { useStrings } from '../../hooks/useStrings';
import { useHaptic } from '../../hooks/useHaptic';
import { useSession } from '../../lib/auth-client';
import { api } from '../../lib/api-client';
import { syncFireAndForget } from '../../lib/sync';
import { SegmentedPicker } from './segmented-picker';
import { ScoreBar } from './score-bar';

const LEVELS: readonly BoundaryLevel[] = ['soft', 'firm', 'pushing'];
const GENDERS: readonly CoachGender[] = ['female', 'male', 'nb'];
const TONES: readonly CoachTone[] = ['dominant', 'playful', 'sensual'];
const INTENSITIES: readonly RitualIntensity[] = ['soft', 'challenging', 'push'];
const COUNT_OPTIONS: readonly number[] = [1, 2, 3, 4, 5, 6, 7];
const PERIODS: readonly RitualPeriod[] = ['day', 'week'];
const LOCALES: readonly Locale[] = ['de', 'en'];

const INTENSITY_DEFAULTS: Record<RitualIntensity, { count: number; period: RitualPeriod }> = {
  soft: { count: 1, period: 'week' },
  challenging: { count: 3, period: 'week' },
  push: { count: 2, period: 'day' },
};

/**
 * Your-personality screen at `/profile/personality`. Surfaces every input that
 * feeds the coach's system prompt (coach details, boundaries, safe word, Big
 * Five narrative, ritual cadence, locale) and lets the user edit each one in
 * place. Big Five is read-only — the canonical edit path is to retake the test.
 */
export function YourPersonalityPage() {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const { strings, locale, setLocale } = useStrings();
  const t = strings.yourPersonality;

  // Account (better-auth session) — the user's own name/email, distinct from the coach.
  const session = useSession();

  // Coach
  const coach = useCoachStore();
  const [nameDraft, setNameDraft] = useState(coach.name ?? '');

  // Covenant
  const boundaries = useCovenantStore((s) => s.boundaries);
  const safeWord = useCovenantStore((s) => s.safeWord);
  const setBoundary = useCovenantStore((s) => s.setBoundary);
  const setSafeWord = useCovenantStore((s) => s.setSafeWord);
  const [safeWordDraft, setSafeWordDraft] = useState(safeWord);

  // Personality
  const personality = usePersonalityStore();

  // Ritual
  const ritual = useRitualStore();

  const back = () => {
    haptic.tap();
    navigate('/profile');
  };

  const commitName = () => {
    const trimmed = nameDraft.trim();
    if (trimmed.length === 0 || trimmed === coach.name) return;
    coach.setName(trimmed);
  };

  const commitSafeWord = () => {
    if (safeWordDraft.trim() === safeWord) return;
    setSafeWord(safeWordDraft);
  };

  const pickIntensity = (next: RitualIntensity) => {
    haptic.tap();
    const defaults = INTENSITY_DEFAULTS[next];
    ritual.setCadence({ count: defaults.count, period: defaults.period, intensity: next });
  };

  const pickCount = (next: number) => {
    haptic.tap();
    ritual.setCadence({
      count: next,
      period: ritual.period ?? 'week',
      // Hand-picking the count breaks the intensity preset — drop the chip.
      intensity: null,
    });
  };

  const pickPeriod = (next: RitualPeriod) => {
    haptic.tap();
    ritual.setCadence({
      count: ritual.count ?? INTENSITY_DEFAULTS[ritual.intensity ?? 'soft'].count,
      period: next,
      intensity: null,
    });
  };

  const onRetake = () => {
    haptic.tap();
    personality.retake();
    navigate('/personality-test');
  };

  const onTake = () => {
    haptic.tap();
    // No DB row → just reopen the routing gate locally; server side already
    // has personalityTestSeen=true (set by skip), so call retake() to flip it.
    personality.retake();
    navigate('/personality-test');
  };

  const scores = personality.scores;
  const narrative = personality.narrative;
  const hasProfile = scores !== null;

  return (
    <div className="mx-auto w-full max-w-md space-y-5 px-4 py-5">
      <header className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={back}
          aria-label={t.backAria}
          className="rounded-full"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-serif text-2xl leading-tight">{t.title}</h1>
          <p className="text-xs text-muted-foreground">{t.intro}</p>
        </div>
      </header>

      {/* You (account) */}
      {session.data && (
        <UserSection
          key={session.data.user.id}
          name={session.data.user.name}
          email={session.data.user.email}
          strings={t.sections.you}
          onCommitName={(name) =>
            syncFireAndForget(async () => {
              await api.me.updateUser({ name });
              await session.refetch?.();
            })
          }
        />
      )}

      {/* Coach */}
      <Card className="space-y-4 border-border bg-card p-5">
        <SectionTitle title={t.sections.coach.title} />

        <Field label={t.sections.coach.nameLabel}>
          <Input
            value={nameDraft}
            placeholder={t.sections.coach.namePlaceholder}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            className="rounded-xl"
          />
        </Field>

        <Field label={t.sections.coach.genderLabel}>
          <SegmentedPicker
            options={GENDERS}
            value={coach.gender}
            onChange={(next) => {
              haptic.tap();
              coach.setGender(next);
            }}
            labelFor={(g) => strings.coach.genders[g].label}
            ariaLabel={t.sections.coach.genderLabel}
          />
        </Field>

        <Field label={t.sections.coach.toneLabel}>
          <SegmentedPicker
            options={TONES}
            value={coach.tone}
            onChange={(next) => {
              haptic.tap();
              coach.setTone(next);
            }}
            labelFor={(tn) => strings.coach.tones[tn].label}
            ariaLabel={t.sections.coach.toneLabel}
          />
        </Field>
      </Card>

      {/* Boundaries */}
      <Card className="space-y-4 border-border bg-card p-5">
        <SectionTitle title={t.sections.boundaries.title} hint={t.sections.boundaries.hint} />
        {BOUNDARY_CATEGORIES.map((category) => (
          <Field
            key={category}
            label={strings.onboarding.covenant.categories[category].label}
            description={strings.onboarding.covenant.categories[category].description}
          >
            <SegmentedPicker
              options={LEVELS}
              value={boundaries[category as BoundaryCategory]}
              onChange={(next) => {
                haptic.tap();
                setBoundary(category, next);
              }}
              labelFor={(lv) => strings.onboarding.covenant.levels[lv]}
              ariaLabel={strings.onboarding.covenant.categories[category].label}
            />
          </Field>
        ))}
      </Card>

      {/* Safe word */}
      <Card className="space-y-3 border-border bg-card p-5">
        <SectionTitle title={t.sections.safeWord.title} hint={t.sections.safeWord.hint} />
        <Input
          value={safeWordDraft}
          placeholder={t.sections.safeWord.placeholder}
          onChange={(e) => setSafeWordDraft(e.target.value)}
          onBlur={commitSafeWord}
          className="rounded-xl"
        />
      </Card>

      {/* Personality */}
      <Card className="space-y-4 border-border bg-card p-5">
        <SectionTitle title={t.sections.personality.title} hint={t.sections.personality.hint} />

        {hasProfile && scores ? (
          <>
            <div className="space-y-3">
              <ScoreBar label={t.sections.personality.traits.openness} value={scores.openness} />
              <ScoreBar
                label={t.sections.personality.traits.conscientiousness}
                value={scores.conscientiousness}
              />
              <ScoreBar
                label={t.sections.personality.traits.extraversion}
                value={scores.extraversion}
              />
              <ScoreBar
                label={t.sections.personality.traits.agreeableness}
                value={scores.agreeableness}
              />
              <ScoreBar
                label={t.sections.personality.traits.neuroticism}
                value={scores.neuroticism}
              />
            </div>

            {narrative && (
              <div className="space-y-2 rounded-xl border border-border/60 bg-background/40 p-4">
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  {t.sections.personality.narrativeEyebrow}
                </p>
                <p className="font-serif italic text-sm leading-relaxed text-foreground/90">
                  {narrative}
                </p>
              </div>
            )}

            <Button
              variant="outline"
              onClick={onRetake}
              className="w-full rounded-xl border-border"
            >
              {t.sections.personality.retakeCta}
            </Button>
          </>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t.sections.personality.emptyState}</p>
            <Button onClick={onTake} className="w-full rounded-xl">
              {t.sections.personality.takeCta}
            </Button>
          </div>
        )}
      </Card>

      {/* Ritual */}
      <Card className="space-y-4 border-border bg-card p-5">
        <SectionTitle title={t.sections.ritual.title} hint={t.sections.ritual.hint} />

        <SegmentedPicker
          options={INTENSITIES}
          value={ritual.intensity}
          onChange={pickIntensity}
          labelFor={(i) => t.sections.ritual.intensities[i]}
          ariaLabel={t.sections.ritual.title}
        />

        <Field label={t.sections.ritual.countLabel}>
          <SegmentedPicker
            options={COUNT_OPTIONS}
            value={ritual.count}
            onChange={pickCount}
            labelFor={(n) => `${n}${t.sections.ritual.timesGlyph}`}
          />
        </Field>

        <SegmentedPicker
          options={PERIODS}
          value={ritual.period}
          onChange={pickPeriod}
          labelFor={(p) => t.sections.ritual.periods[p]}
        />
      </Card>

      {/* Language */}
      <Card className="space-y-3 border-border bg-card p-5">
        <SectionTitle title={t.sections.language.title} />
        <SegmentedPicker
          options={LOCALES}
          value={locale}
          onChange={(next) => {
            haptic.tap();
            setLocale(next);
          }}
          labelFor={(l) => t.sections.language[l]}
          ariaLabel={t.sections.language.title}
        />
      </Card>

    </div>
  );
}

function UserSection({
  name,
  email,
  strings,
  onCommitName,
}: {
  name: string;
  email: string;
  strings: { title: string; nameLabel: string; namePlaceholder: string; emailLabel: string };
  onCommitName: (name: string) => void;
}) {
  const [nameDraft, setNameDraft] = useState(name ?? '');

  const commitName = () => {
    const trimmed = nameDraft.trim();
    if (trimmed.length === 0 || trimmed === name) return;
    onCommitName(trimmed);
  };

  return (
    <Card className="space-y-4 border-border bg-card p-5">
      <SectionTitle title={strings.title} />

      <Field label={strings.nameLabel}>
        <Input
          value={nameDraft}
          placeholder={strings.namePlaceholder}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={commitName}
          className="rounded-xl"
        />
      </Field>

      <Field label={strings.emailLabel}>
        <Input value={email} readOnly disabled className="rounded-xl" />
      </Field>
    </Card>
  );
}

function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="space-y-1">
      <h2 className="font-serif text-lg leading-tight">{title}</h2>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Field({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div>
        <div className="text-sm text-foreground/85">{label}</div>
        {description && <div className="text-xs text-muted-foreground">{description}</div>}
      </div>
      {children}
    </div>
  );
}
